<!-- In the chat window's frame (src/_cura-window.scss, panel class ai-recording-panel), as the
     recording dialog is. The list keeps its Material list structure and every action. -->
<div class="cw-header">
  <mat-icon class="kind">mic_off</mat-icon>
  <div class="title">
    <!-- "Failed" was the only thing this list could hold until `abandoned` existed. It now also
         holds consultations where nothing failed at all, so the title has to cover both. -->
    <span class="name">Unprocessed recordings</span>
    <span class="subtitle" *ngIf="failedRecordings.length > 0; else allDone">
      {{ failedRecordings.length }} kept on this device, waiting to be processed
    </span>
    <ng-template #allDone><span class="subtitle">Everything recorded on this device has been processed</span></ng-template>
  </div>
</div>

<mat-dialog-content class="failed-recordings-content cw-body">
  <!-- Storage Usage -->
  <div class="storage-usage cw-card" *ngIf="failedRecordings.length > 0">
    <div class="storage-info">
      <mat-progress-bar [value]="storageUsagePercent" [color]="storageColor" mode="determinate">
      </mat-progress-bar>
      <div class="storage-text">
        <span>{{ storageUsed | number:'1.0-1' }} / {{ storageTotal | number:'1.0-1' }} MB of your device's local storage
          used</span>
        <mat-icon *ngIf="storageUsagePercent > 80" color="warn" matTooltip="Storage nearly full">warning</mat-icon>
      </div>
    </div>
    <div *ngIf="storageWarning" class="storage-warning">
      <mat-icon>warning</mat-icon>
      <span>Storage is {{ storageUsagePercent | number:'1.0-0' }}% full. Consider deleting old recordings.</span>
    </div>
  </div>

  <!-- Recordings List -->
  <div class="recordings-list" *ngIf="failedRecordings.length > 0">
    <mat-list>
      <mat-list-item *ngFor="let recording of failedRecordings" class="recording-item">
        <ng-container *ngIf="getRecordingComputedData(recording.id) as computed">
          <div class="recording-item-wrapper">
            <div class="recording-info">
              <span class="date-time">{{ recording.createdAt | date:'short' }}</span>
              <span class="patient-name">{{ recording.patientName || 'Unknown Patient' }}</span>
              <span class="file-size">{{ recordingSizes.get(recording.id) || 'Loading...' }}</span>
              <span class="recording-outcome" [ngClass]="'outcome-' + computed.outcome.tone"
                [matTooltip]="computed.outcome.detail">
                <mat-icon class="outcome-icon">{{ computed.outcome.icon }}</mat-icon>
                {{ computed.outcome.label }}
              </span>
            </div>

            <div class="recording-actions">
              <!-- Retry button (only show if not completed and not currently retrying) -->
              <button mat-icon-button *ngIf="!computed.hasFinalResult && !computed.isRetrying"
                (click)="retryRecording(recording)" class="retry"
                [matTooltip]="computed.outcome.tone === 'unfinished' ? 'Process Recording' : 'Retry Processing'">
                <mat-icon>{{ computed.outcome.tone === 'unfinished' ? 'play_arrow' : 'refresh' }}</mat-icon>
              </button>

              <!-- View button (show when retry completed successfully, matching ai-dialog pattern) -->
              <button mat-raised-button *ngIf="computed.hasFinalResult" (click)="viewResult(recording)"
                matTooltip="View Generated Notes" class="view-button tone-button">
                View
              </button>

              <!-- Retry indicator (just a spinner, status shown at bottom) -->
              <mat-spinner *ngIf="computed.isRetrying" diameter="20" class="retry-spinner"></mat-spinner>

              <button mat-icon-button (click)="downloadRecording(recording)" matTooltip="Download Recording"
                [disabled]="computed.isRetrying">
                <mat-icon>download</mat-icon>
              </button>

              <button mat-icon-button class="delete" (click)="deleteRecording(recording)" matTooltip="Delete Recording"
                [disabled]="computed.isRetrying">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          </div>

          <!-- Download Log Section (shows for both success and failure) -->
          <div class="download-log-section" *ngIf="SHOW_LOGS && hasDownloadLogs(recording.id)">
            <div class="log-header" (click)="toggleLogExpansion(recording.id)">
              <mat-icon [class.failed]="hasDownloadError(recording.id)" class="log-icon">
                {{ hasDownloadError(recording.id) ? 'error' : 'info' }}
              </mat-icon>
              <span class="log-title">
                {{ hasDownloadError(recording.id) ? 'Download Failed' : 'Download Succeeded' }} -
                Process log ({{ getDownloadLogs(recording.id).length }} entries)
              </span>
              <mat-icon class="expand-icon" [class.expanded]="isLogExpanded(recording.id)">
                {{ isLogExpanded(recording.id) ? 'expand_less' : 'expand_more' }}
              </mat-icon>
            </div>

            <div class="log-content" *ngIf="isLogExpanded(recording.id)">
              <div class="log-actions">
                <button mat-icon-button (click)="copyLogToClipboard(recording.id); $event.stopPropagation()"
                  matTooltip="Copy process log to clipboard" [class.copied]="copySuccess.get(recording.id)">
                  <mat-icon>{{ copySuccess.get(recording.id) ? 'check_circle' : 'content_copy' }}</mat-icon>
                </button>
              </div>
              <div class="log-entries">
                <div *ngFor="let entry of getDownloadLogs(recording.id); let i = index" class="log-entry"
                  [class.log-entry-info]="entry.level === 'info'" [class.log-entry-success]="entry.level === 'success'"
                  [class.log-entry-warn]="entry.level === 'warn'" [class.log-entry-error]="entry.level === 'error'">
                  <div class="log-entry-header">
                    <span class="log-entry-index">[{{ i + 1 }}]</span>
                    <span class="log-entry-level" [class]="'level-' + entry.level">{{ entry.level.toUpperCase()
                      }}</span>
                    <span class="log-entry-time">{{ entry.timestamp | date:'HH:mm:ss.SSS' }}</span>
                  </div>
                  <div class="log-entry-message">{{ entry.message }}</div>
                  <div class="log-entry-details" *ngIf="hasLogDetails(entry)">
                    <pre>{{ formatLogDetails(entry.details) }}</pre>
                  </div>
                  <div class="log-entry-stack" *ngIf="entry.stackTrace">
                    <details>
                      <summary>Stack Trace</summary>
                      <pre>{{ entry.stackTrace }}</pre>
                    </details>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ng-container>
      </mat-list-item>
    </mat-list>
  </div>

  <!-- Empty State -->
  <div class="empty-state" *ngIf="failedRecordings.length === 0">
    <mat-icon>check_circle</mat-icon>
    <p>No failed recordings</p>
    <span class="cw-hint">All recordings have been successfully processed</span>
  </div>

  <!-- Status Messages at Bottom (shows current retry status) -->
  <div class="retry-status-bottom" *ngIf="retryingRecordings.size > 0">
    <div *ngFor="let recordingId of retryingRecordingIds" class="cw-status pending status-message">
      <ng-container *ngIf="getRecordingComputedData(recordingId) as computed">
        <mat-spinner diameter="24" class="status-spinner"></mat-spinner>
        <div class="status-content">
          <div class="status-title">
            {{ computed.patientName }} - Retry in Progress
          </div>
          <div class="status-text" *ngIf="computed.retryStatus">
            {{ computed.retryStatus.message }}
          </div>
        </div>
      </ng-container>
    </div>
  </div>
</mat-dialog-content>

<mat-dialog-actions class="cw-actions">
  <span class="spacer"></span>
  <button mat-stroked-button class="quiet-button" mat-dialog-close>Close</button>
</mat-dialog-actions>
